import React, { forwardRef, HTMLAttributes, ReactNode, createContext, useCallback, useEffect, useLayoutEffect, useRef, useState, useId } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import { Transition } from 'react-transition-group'
import { FocusTrap } from 'focus-trap-react'
// evertel
import { useForkedRef } from '@evertel/hooks'
import { Backdrop } from '../Backdrop'
import ModalContent from './ModalContent'
import ModalDialog from './ModalDialog'

interface Props extends HTMLAttributes<HTMLDivElement> {
    /** Align the modal in the center or top of the screen. */
    alignment?: 'top'|'center'
    /** Align the modal in the center or top of the screen. */
    backdrop?: boolean|'static'
    /** Transition time to open/close in milliseconds */
    duration?: number
    /** Set modal to covers the entire user viewport. */
    fullscreen?: boolean|'sm'|'md'|'lg'|'xl'|'xxl'
    /** Closes the modal when escape key is pressed. */
    keyboard?: boolean
    onClose?: () => void
    onCloseDone?: () => void
    /** Callback fired when the modal is shown, its backdrop is static and a click outside the modal or an escape key press is performed with the keyboard option set to false. */
    onClosePrevented?: () => void
    onShow?: () => void
    /** Generates modal using createPortal. */
    portal?: boolean
    scrollable?: boolean
    size?: 'sm'|'md'|'lg'|'xl'|'xxl'
    /** Remove animation to create modal that simply appear rather than fade in to view. */
    transition?: boolean
    /** By default the component is unmounted after close animation, if you want to keep the component mounted set this property to false. */
    unmountOnClose?: boolean
    visible?: boolean
    children: ReactNode
    className?: string
}

interface ModalContextProps {
    visible?: boolean
    setVisible: React.Dispatch<React.SetStateAction<boolean|undefined>>
    handleClose?: () => void
}

export const ModalContext = createContext({} as ModalContextProps)

// Global modal stack
export const modalStackMap = new Map()

const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const Modal = forwardRef<HTMLDivElement, Props>(({
    alignment,
    backdrop = true,
    duration = 150,
    fullscreen,
    keyboard = true,
    onClose,
    onCloseDone,
    onClosePrevented,
    onShow,
    portal = true,
    scrollable,
    size,
    transition = true,
    unmountOnClose = true,
    visible = false,
    children,
    className,
    ...otherProps
}, ref) => {

    const modalId = useId()
    const modalRef = useRef<HTMLDivElement>(null)
    const modalContentRef = useRef<HTMLDivElement>(null)
    const forkedRef = useForkedRef(ref, modalContentRef)
    const previouslyFocusedElement = useRef<HTMLElement | null>(null)

    const [_visible, setVisible] = useState(visible)
    const [staticBackdrop, setStaticBackdrop] = useState(false)
    const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)

    useEffect(() => {
        setVisible(visible)
    }, [visible])

    // prevent dependency lists from triggering too much
    //   otherwise the modalStack gets out of order
    const closeRef = useRef<(() => void) | undefined>(undefined)
    closeRef.current = onClose
    const closePreventedRef = useRef<(() => void) | undefined>(undefined)
    closePreventedRef.current = onClosePrevented

    const handleClose = useCallback(() => {
        if (closeRef.current) {
            closeRef.current()
        } else {
            setVisible(false)
        }
    }, [])

    const handleDismiss = useCallback(() => {
        if (backdrop === 'static') {
            setStaticBackdrop(true)
            closePreventedRef.current && closePreventedRef.current()
            return
        }
        return handleClose()
    }, [handleClose])

    const contextValues = {
        visible: _visible,
        setVisible,
        handleClose
    }

    // ancestors of the opener, nearest first, recorded at open time. If the opener has been
    // re-rendered away by the time we close (a table refresh, a list re-fetch), a detached node
    // can't lead us back to the document, but its recorded ancestors can
    const previouslyFocusedAncestors = useRef<HTMLElement[]>([])

    const getRestoreTarget = (): HTMLElement | null => {
        const opener = previouslyFocusedElement.current
        if (!opener || typeof opener.focus !== 'function') return null
        if (document.contains(opener)) return opener

        const survivor = previouslyFocusedAncestors.current.find(node => document.contains(node))
        if (!survivor) return null

        // land on the first control inside the nearest surviving ancestor (e.g. the first row of
        // the refreshed table) so the user is still where they were, not back at the top
        return survivor.querySelector<HTMLElement>(focusableSelector) ?? survivor
    }

    const restorePreviousFocus = () => {
        setTimeout(() => {
            // need to give focus trap some time to close up
            const target = getRestoreTarget()
            if (!target) return

            // a plain container has to be made programmatically focusable to accept focus (and so
            // set the Tab starting point); undo that once focus moves on
            if (!target.matches(focusableSelector) && !target.hasAttribute('tabindex')) {
                target.setAttribute('tabindex', '-1')
                target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true })
            }
            target.focus()
        }, 100)
    }

    useEffect(() => {
        if (visible && !_visible) {
            // Modal is about to open
            const opener = document.activeElement as HTMLElement
            previouslyFocusedElement.current = opener

            const ancestors: HTMLElement[] = []
            for (let node = opener?.parentElement; node && node !== document.body; node = node.parentElement) {
                ancestors.push(node)
            }
            previouslyFocusedAncestors.current = ancestors
        } else if (!visible && _visible) {
            // Modal is about to close
            restorePreviousFocus()
        }
    }, [visible, _visible])

    // a modal that is conditionally rendered (`{thing && <Modal visible />}`) unmounts while
    // still open, so the close branch above never runs; hand focus back from the unmount too
    const isOpenRef = useRef(false)
    isOpenRef.current = _visible
    useEffect(() => () => {
        if (isOpenRef.current) restorePreviousFocus()
    }, [])

    useEffect(() => {
        if (_visible) {
            modalStackMap.set(modalId, handleDismiss)
        } else {
            modalStackMap.delete(modalId)
        }

        return () => {
            modalStackMap.delete(modalId)
        }
    }, [_visible, handleDismiss])

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            //this ensures that the mouse down and up happen in the same place
            setMouseDownTarget(event.target)
        }
    }, [])

    const handleMouseUp = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && event.target === mouseDownTarget) {
            //currentTarget is the object the event was attached to, only allowing dismissal if the background was directly clicked
            handleDismiss()
        }
        setMouseDownTarget(null)
    }, [mouseDownTarget, handleDismiss])

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            // call the last item in the modal stack
            const [, topDismiss] = [...modalStackMap].at(-1) || []
            topDismiss && topDismiss()
        }
    }, [])

    useEffect(() => {
        // these event listeners never get called because focus-trap blocks it
        // but keeping so we can easily disable focus-trap for testing if we need to

        // might need to coordinate this being attached to document to get around focus-trap
        if (_visible && modalRef.current) {
            modalRef.current.addEventListener('keydown', handleKeyDown)
        }

        return () => {
            if (modalRef.current) {
                modalRef.current.removeEventListener('keydown', handleKeyDown)
            }
        }
    }, [_visible])

    useLayoutEffect(() => {
        if (staticBackdrop) {
            setTimeout(() => setStaticBackdrop(false), duration)
        }
    }, [staticBackdrop, duration])

    const getTransitionClass = (state: string) => {
        return state === 'entering'
            ? 'd-block'
            : state === 'entered'
                ? 'show d-block'
                : state === 'exiting'
                    ? 'd-block'
                    : ''
    }

    const _className = classNames(
        'modal',
        {
            'modal-static': staticBackdrop,
            fade: transition
        },
        className
    )

    // set focus to modal after open
    useLayoutEffect(() => {
        if (_visible) {
            document.body.classList.add('modal-open')

            if (backdrop) {
                document.body.style.overflow = 'hidden'
                document.body.style.paddingRight = '0px'
            }

            setTimeout(() => {
                modalContentRef.current?.focus()
            }, (!transition) ? 0 : duration)

        } else {
            if (modalStackMap.size === 0) {
                document.body.classList.remove('modal-open')

                if (backdrop) {
                    document.body.style.removeProperty('overflow')
                    document.body.style.removeProperty('padding-right')
                }
            }
        }
        return () => {
            if (modalStackMap.size === 0) {
                document.body.classList.remove('modal-open')
                if (backdrop) {
                    document.body.style.removeProperty('overflow')
                    document.body.style.removeProperty('padding-right')
                }
            }
        }
    }, [_visible, backdrop, transition, duration])

    const modal = (jointRef?: React.Ref<HTMLDivElement>, transitionClass?: string, state?: string) => {
        const modalContent = (
            <div
                className={classNames(_className, transitionClass)}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                tabIndex={-1}
            >
                <ModalDialog
                    alignment={alignment}
                    fullscreen={fullscreen}
                    scrollable={scrollable}
                    size={size}
                >
                    <ModalContent
                        ref={jointRef}
                        {...otherProps}
                        tabIndex={-1}
                        role="dialog"
                        aria-modal="true"
                    >
                        {children}
                    </ModalContent>
                </ModalDialog>
            </div>
        )

        // leave this, necessary for testing
        // return modalContent
        return (_visible) ? (
            <FocusTrap
                focusTrapOptions={{
                    returnFocusOnDeactivate: false,
                    initialFocus: () => modalContentRef.current,
                    // escapeDeactivates: false,
                    escapeDeactivates: (event: KeyboardEvent) => {

                        // this doesn't actually stop the SlidePanel from closing
                        // doesn't seem to have any affect
                        event.preventDefault()
                        event.stopPropagation()
                        event.stopImmediatePropagation()

                        handleKeyDown(event)
                        return false
                    },
                    allowOutsideClick: true,
                    fallbackFocus: '.modal',
                    preventScroll: true
                    // since we're already managing most state ourselves, lets not use FocusTrap
                    // onDeactivate: () => {
                    //     if (previouslyFocusedElement.current && typeof previouslyFocusedElement.current.focus === 'function') {
                    //         setTimeout(() => {
                    //             previouslyFocusedElement.current?.focus()
                    //         }, 0)
                    //     }
                    // }
                }}
            >
                {modalContent}
            </FocusTrap>
        ) : (modalContent)
    }

    const content = (
        <ModalContext.Provider value={contextValues}>
            <Transition
                in={_visible}
                mountOnEnter
                nodeRef={modalRef}
                onEnter={onShow}
                onExit={() => { /** */ }}
                onExited={onCloseDone}
                unmountOnExit={unmountOnClose}
                timeout={(!transition) ? 0 : duration}>
                {(state) => {
                    const transitionClass = getTransitionClass(state)
                    return modal(forkedRef, transitionClass, state)
                }}
            </Transition>
            {backdrop && (
                <Backdrop
                    visible={_visible}
                />
            )}
        </ModalContext.Provider>
    )

    return (typeof window !== 'undefined' && portal)
        ? createPortal(content, document.body)
        : content
})

export default Modal
Modal.displayName = 'Modal'
